import * as CL from '@design-system/component-library';
import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { deviceListsMsg, filterMsg, omaElisaForCompaniesMsg, searchMsg, t } from '../../common/i18n/index.js';
import {
  type CatalogProduct,
  SupportedModelCategories,
  SupportedModelCategory,
} from '../../common/utils/catalogUtils.js';
import { FilterTag } from '../FilterTag/FilterTag';
import { Grid } from '../Grid/Grid.js';
import { HeroHeading, HeroHeadingType } from '../HeroHeading/index.js';
import { NavigationMenu, NavigationMenuItem } from '../NavigationMenu/NavigationMenu.js';
import { ProductGridFilter, createFilters } from '../ProductGrid/ProductGridFilter.js';
import { ProductGridSearchCL } from '../ProductGrid/ProductGridSearch.js';
import { ProductGridSort, SortType } from '../ProductGrid/ProductGridSort.js';
import { CatalogProductSelectionFooter } from './CatalogProductSelectionFooter';
import { getProductCategoryDetails, sortAndFilterProducts } from './CatalogProductSelectionUtils.js';
import { CatalogSelectionDeviceTable } from './CatalogSelectionDeviceTable.js';
import { DiscontinuedDevicesAccordion } from './DiscontinuedDevicesAccordion';
import type { DeviceSelection } from './CatalogProductSelectionFormWrapper.js';
import type { Catalog } from '../../generated/api/catalog';
import type { Filters } from '../ProductGrid/ProductGridFilter.js';

export interface CatalogProductsSelectionFormProps {
  contractPeriod?: number;
  imagesBaseUrl: string;
  catalogProducts: Record<string, CatalogProduct[]>;
  catalog: Catalog;
}

interface NavigationProps {
  onChangeCategory: (SupportedCat: SupportedModelCategory) => void;
  currentCategory?: SupportedModelCategory;
}

export const Navigation = ({ onChangeCategory, currentCategory }: NavigationProps) => (
  <NavigationMenu>
    {SupportedModelCategories.map(category => {
      const { displayText, iconClass } = getProductCategoryDetails(category);
      return (
        <>
          <NavigationMenuItem
            key={category}
            id={category}
            linkType="clickable"
            icon={<CL.Icon color="brand-blue" icon={iconClass} size="l" />}
            label={displayText}
            isActive={currentCategory === category}
            onClick={() => {
              onChangeCategory(category);
            }}
          />
        </>
      );
    })}
  </NavigationMenu>
);

export const CatalogProductSelectionForm = ({
  catalogProducts,
  imagesBaseUrl,
  contractPeriod,
  catalog,
}: CatalogProductsSelectionFormProps) => {
  const { getValues } = useFormContext<DeviceSelection>();
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [sortType, setSortType] = useState(SortType.ORDER_NEWEST);
  const [searchString, setSearchString] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<Record<string, Filters>>({});
  const [productCategory, setProductCategory] = useState<SupportedModelCategory>(SupportedModelCategory.PHONE);
  const [orderedCatalogProducts, setOrderedCatalogProducts] =
    useState<Record<string, CatalogProduct[]>>(catalogProducts);

  useEffect(() => {
    if (Object.keys(selectedFilters).length === 0) {
      return;
    }

    setOrderedCatalogProducts(sortAndFilterProducts(searchString, catalogProducts, sortType, selectedFilters));
  }, [catalogProducts, searchString, selectedFilters, sortType]);

  if (!selectedFilters[productCategory]) {
    const filters: Record<string, Filters> = {};
    SupportedModelCategories.forEach(category => {
      filters[category] = createFilters(catalogProducts[category], category);
    });
    setSelectedFilters(filters);
    setOrderedCatalogProducts(sortAndFilterProducts(searchString, catalogProducts, sortType, filters));
  }

  const onChangeCategory = (value: SupportedModelCategory) => {
    setProductCategory(value);
    setOrderedCatalogProducts(sortAndFilterProducts(searchString, catalogProducts, sortType, selectedFilters));
  };

  const onChangeFilters = (updatedFilters: Filters) => {
    const newFilters = { ...selectedFilters, [productCategory]: updatedFilters };
    setSelectedFilters(newFilters);
    setOrderedCatalogProducts(sortAndFilterProducts(searchString, catalogProducts, sortType, newFilters));
  };

  const onSearch = (value: string) => {
    setSearchString(value);
    setOrderedCatalogProducts(sortAndFilterProducts(value, catalogProducts, sortType, selectedFilters));
  };

  const onChangeSortType = (value: SortType) => {
    setSortType(value);
    setOrderedCatalogProducts(sortAndFilterProducts(searchString, catalogProducts, value, selectedFilters));
  };

  const toggleFilters = () => {
    setFiltersExpanded(!filtersExpanded);
  };

  const onShowSelectedProductsFirst = () => {
    const selectedProductCodes = getValues('selectedProductCodes') || [];
    const sortedProducts = { ...orderedCatalogProducts };
    sortedProducts[productCategory] = [
      ...orderedCatalogProducts[productCategory].filter(p => selectedProductCodes.includes(p.code)),
      ...orderedCatalogProducts[productCategory].filter(p => !selectedProductCodes.includes(p.code)),
    ];
    setOrderedCatalogProducts(sortedProducts);
  };

  const getAllSelectedFilters = (filtersObject: Record<string, { selectedOptions: string[] }>) => {
    if (!filtersObject) {
      return [];
    }

    return Object.entries(filtersObject).flatMap(([subcategory, filter]) => ({
      subcategory,
      selectedOptions: filter.selectedOptions,
    }));
  };

  const selectedFilterCount = Object.values(selectedFilters[productCategory] || {}).reduce(
    (count, filter) => count + filter.selectedOptions.length,
    0
  );

  const handleRemoveFilter = (subcategory: string, label: string) => {
    const productCategoryFilters = selectedFilters[productCategory];
    const currentCategoryFilters = productCategoryFilters[subcategory as keyof Filters];
    const current = {
      ...productCategoryFilters,
      [subcategory]: {
        ...currentCategoryFilters,
        selectedOptions: currentCategoryFilters.selectedOptions.filter((option: string) => option !== label),
      },
    };
    onChangeFilters(current);
  };

  return (
    <>
      <HeroHeading
        center={false}
        breadCrumbPaths={[
          { name: t.VCUZ(omaElisaForCompaniesMsg), path: paths.SELF_SERVICE_HOME },
          { name: t.COBB(deviceListsMsg), path: paths.COMPANY_INFO_CATALOGS },
          { name: catalog.name, path: paths.COMPANY_INFO_CATALOG_LIST_PRODUCTS },
          { name: getProductCategoryDetails(productCategory).displayText },
        ]}
        heroHeadingType={HeroHeadingType.BLANK}
        title={catalog.name}
        subtitle={
          contractPeriod
            ? `${t.V3UP('Device contract period')} ${contractPeriod} ${t.UGPA('month(s). Quoted prices VAT 0%')}`
            : undefined
        }
      />
      <DiscontinuedDevicesAccordion discontinuedDevices={catalog.discontinuedProducts ?? []} />
      <div className="of-catalog-products-selection__border-bottom">
        <Grid colWidthL={10}>
          <Navigation onChangeCategory={onChangeCategory} currentCategory={productCategory} />
        </Grid>
      </div>
      <CL.Grid className={dsClass.PADDING_TOP_2}>
        <CL.GridRow justifyCenter>
          <CL.GridCol colsXS={4} colsM={6} colsL={10} colsXL={10}>
            <div className="of-catalog-products-selection__sortandfilter">
              <div className={dsClass.MARGIN_RIGHT_3}>
                <ProductGridSearchCL placeholder={t.VQE6(searchMsg)} onChange={onSearch} value={searchString} />
              </div>
              <div>
                <CL.Button type="button" color="link" onClick={toggleFilters}>
                  <span>
                    <span className="of-file-input--controls--button-text">{t.XUFV(filterMsg)}</span>
                    <CL.Icon className={dsClass.MARGIN_LEFT_2} icon="filter" size="s" />
                  </span>
                </CL.Button>
              </div>
              <div>
                <ProductGridSort onChange={onChangeSortType} />
              </div>
              <div>
                <CL.Button type="button" color="link" onClick={onShowSelectedProductsFirst}>
                  <span>
                    <span className="of-file-input--controls--button-text">{t.P9S4('Show selected first')}</span>
                    <CL.Icon className={dsClass.MARGIN_LEFT_2} icon="update" size="s" />
                  </span>
                </CL.Button>
              </div>
            </div>
          </CL.GridCol>
        </CL.GridRow>
      </CL.Grid>
      {selectedFilterCount > 0 && (
        <Grid colWidthL={10} key="selectedFilters" className="of-catalog-products-selection__filter-tags">
          <div className="of-catalog-products-selection__filter-tags-container">
            {getAllSelectedFilters(selectedFilters[productCategory]).map(filter =>
              filter.selectedOptions.map((selectedOption: string, index: number) => (
                <FilterTag
                  subcategory={filter.subcategory}
                  label={selectedOption}
                  key={`${filter.subcategory}-${index}`}
                  onRemoveFilter={() => handleRemoveFilter(filter.subcategory, selectedOption)}
                />
              ))
            )}
          </div>
        </Grid>
      )}
      {filtersExpanded && selectedFilters[productCategory] && (
        <Grid colWidthL={10}>
          <div className={`of-catalog-products-selection__filter-content ${dsClass.FONT_SIZE_SMALL}`}>
            <ProductGridFilter
              filters={selectedFilters[productCategory]}
              onChange={(updatedFilters: Filters) => {
                onChangeFilters(updatedFilters);
              }}
              onClose={() => setFiltersExpanded(false)}
            />
          </div>
        </Grid>
      )}
      <Grid colWidthL={10} key={productCategory}>
        <CatalogSelectionDeviceTable
          products={orderedCatalogProducts[productCategory]}
          imagesBaseUrl={imagesBaseUrl}
          accessories={orderedCatalogProducts.ACCESSORIES}
          productCategory={productCategory}
        />
      </Grid>
      <CatalogProductSelectionFooter catalogProducts={catalogProducts} />
    </>
  );
};
